// Copyright (c) Docugami, Inc. All rights reserved.

import { useUserDocSetsNavigationGroup } from "hooks/DocSetNavigationGroup";
import { ExplorerViewType, NavigationGroup } from "components/myContentExplorer/ExplorerTypes";
import { NavigationListItem } from "components/myContentExplorer/NavigationItemList";
import { useReviewDocsNavigationGroup } from "hooks/ReviewDocsNavigationGroup";
import { BasicIconProps } from "components/work_path/IconRenderer";
import { useDocSourceNavigationGroup } from "./DocSourceNavigationGroup";
import { useEffect, useMemo, useState } from "react";
import { isValidViewId } from "components/myContentExplorer/ExplorerViewFactory";
import { useSamplesNavigationGroup } from "./SampleNavigationGroup";
import { IExplorerStatusItem } from "./ExplorerStatus";

export interface IExplorerNavigationGroup {
    id: NavigationGroup;
    title: string;
    items: NavigationListItem[];
    icon?: BasicIconProps;
    collapsedItemCount?: number;
    placeholder?: JSX.Element;
    fallbackId?: string;
    onExpand?: () => void;
    statusItems: IExplorerStatusItem[];
}

export interface INavigationGroups {
    items: IExplorerNavigationGroup[],
    selectedGroup?: NavigationGroup,
    selectedType?: ExplorerViewType
}

export function useExplorerNavigationGroups(
    onSelected: (id: string) => void,
    defaultId: string,
    selectedId: string | undefined): INavigationGroups {
    const [previousGroup, setPreviousGroup] = useState<NavigationGroup>();

    const userDocSetsGroup = useUserDocSetsNavigationGroup();
    const samplesGroup = useSamplesNavigationGroup();
    const reviewDocsGroup = useReviewDocsNavigationGroup();
    const docSourcesGroup = useDocSourceNavigationGroup();

    const groups = useMemo((): IExplorerNavigationGroup[] =>
        [docSourcesGroup, reviewDocsGroup, userDocSetsGroup, samplesGroup],
        [userDocSetsGroup, samplesGroup, reviewDocsGroup, docSourcesGroup]);

    const [selectedGroup, selectedType] = useMemo(() => {
        if (!selectedId) {
            return [undefined, undefined];
        }

        for (const group of groups) {
            const index = group.items.findIndex(item => item.id === selectedId);
            if (index !== -1) {
                return [group.id, group.items[index].type];
            }
        }
        return [undefined, undefined];
    }, [selectedId, groups]);

    // This hook handles the scenario where the currently selected item is deleted.
    // It first tries to select a new item from the same group, otherwise it falls back to a default item
    useEffect(() => {
        let localSelectedGroup: NavigationGroup | undefined = selectedGroup;
        if (!!selectedGroup) {
            setPreviousGroup(localSelectedGroup);
            return;
        }

        let newSelectedId = selectedId;
        if (!!selectedId && !localSelectedGroup && !isValidViewId(selectedId)) {
            // We can't find a group for the target selection. Try to select a new ID from the previous group or fall back to default
            if (!!previousGroup) {
                const group = groups.find(x => x.id === previousGroup);
                newSelectedId = group?.items[0]?.id ?? group?.fallbackId;
            }
        }

        // Fallback to default
        if (!newSelectedId) {
            newSelectedId = defaultId;
        }

        if (newSelectedId !== selectedId) {
            onSelected(newSelectedId);
        }
    },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [selectedId, defaultId, groups])

    return {
        items: groups,
        selectedGroup: selectedGroup,
        selectedType: selectedType
    }
}
